import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Search, Edit, Trash2, Eye, Loader2, RefreshCw, ChevronDown, CalendarIcon } from "lucide-react";
import { ImportExportActions, ImportMode, generateScholarshipTemplate } from "./ImportExportUtils";
import { notify } from "@/lib/utils/toast";
import { useAuthStore, PERMISSIONS } from "../lib/stores/auth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { cn } from "./ui/utils";
import { format, parse } from "date-fns";
import { PaginationIndex } from "./PaginationIndex";
import { classificationScholarship, majorListForScholarship } from "./validations/shared";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { ScholarshipModal } from "./modals/ScholarshipModal";
import { ScholarshipDetail } from "./modals/ScholarshipDetail";
import { Scholarship, scholarshipApi, ScholarshipFilters } from "@/lib/api/scholarship";
import { validateScholarshipImportData } from "./validations/scholarship";
import { vi } from "date-fns/locale";
import { NgocInput } from "./ui/ngocInput";
import { AccessDenied } from "./AccessDenied";
import { scholarshipTitleApi } from "@/lib/api/scholarship-title";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function ScholarshipManagement() {
	const { hasPermission } = useAuthStore();
	const [degreeInformation, setDegreeInformation] = useState<Scholarship | null>(null);
	const [degrees, setDegrees] = useState<Scholarship[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [scholarshipTitleFilter, setScholarshipTitleFilter] = useState("");
	const [deleteItemId, setDeleteItemId] = useState<string | undefined>();
	const [modalMode, setModalMode] = useState<"create" | "edit">("create");

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// Modal states
	const [isHandleModalOpen, setHandleModalOpen] = useState(false);
	const [editingDegree, setEditingDegree] = useState<Scholarship | null>(null);

	// Loading states for individual operations
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Permission checks
	const canView = hasPermission(PERMISSIONS.scholarship.permissions.view.key);
	const canCreate = hasPermission(PERMISSIONS.scholarship.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.scholarship.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.scholarship.permissions.delete.key);
	const canImport = hasPermission(PERMISSIONS.scholarship.permissions.import.key);
	const canExport = hasPermission(PERMISSIONS.scholarship.permissions.export.key);

	const [createdDateFromFilterModal, setCreatedDateFromFilterModal] = useState(false);
	const [createdDateToFilterModal, setCreatedDateToFilterModal] = useState(false);
	const [createdDateFromFilter, setCreatedDateFromFilter] = useState("");
	const [createdDateToFilter, setCreatedDateToFilter] = useState("");

	const [updatedDateFromFilterModal, setUpdatedDateFromFilterModal] = useState(false);
	const [updatedDateToFilterModal, setUpdatedDateToFilterModal] = useState(false);
	const [updatedDateFromFilter, setUpdatedDateFromFilter] = useState("");
	const [updatedDateToFilter, setUpdatedDateToFilter] = useState("");
	const [scholarshipTitle, setScholarshipTitle] = useState<string[]>([]);
	const [isFiltering, setIsFiltering] = useState(false);
	const [autoFilters, setAutoFilters] = useState<ScholarshipFilters>({});
	const [majorFilter, setMajorFilter] = useState("");
	const [scholarshipTitleOptions, setScholarshipTitleOptions] = useState<string[]>([]);

	if (!canView) return <AccessDenied name='danh sách Khen thưởng - Chứng nhận' />;

	const [searchMajorName, setSearchMajorName] = useState("");
	const [majors, setMajors] = useState<string[]>([]);
	const [searchScholarshipTitleName, setSearchScholarshipTitleName] = useState("");


	const toggleSelectMajor = (val: string) => {
		const displayData = majors.includes(val) ? majors.filter((v) => v !== val) : [...majors, val];
		setMajors(displayData);
		handleFilterChange(setMajorFilter, displayData);
	};


	const toggleSelectScholarshipTitle = (val: string) => {
		const displayData = scholarshipTitle.includes(val) ? scholarshipTitle.filter((v) => v !== val) : [...scholarshipTitle, val];
		setScholarshipTitle(displayData);
		handleFilterChange(setScholarshipTitleFilter, displayData);
	};

	const getFullRecord = async (filter: ScholarshipFilters = {}) => {
		const result = await scholarshipApi.getAll({
			...filter,
			page: 1,
			limit: 999999
		});
		return result.data.data || [];
	};

	const fetchList = useCallback(async (customFilters?: ScholarshipFilters) => {
		let finalFilter = customFilters || autoFilters;
		try {
			setLoading(true);

			const result = await scholarshipApi.getAll(finalFilter);

			if (result && result.success && result.data) {
				setDegrees(result.data.data || []);
				setTotalCount(result.data.total || 0);
				setTotalPages(result.data.totalPages || 1);
			} else {
				console.warn("API returned unsuccessful result:", result);
				// Set empty data as fallback
				setDegrees([]);
				setTotalCount(0);
				setTotalPages(1);
			}
		} catch (error) {
			console.error("Exception:", error);
			setDegrees([]);
			setTotalCount(0);
			setTotalPages(1);
		} finally {
			setLoading(false);
		}
	}, [autoFilters]);

	// fetch title options
	async function fetchScholarshipTitleOptions() {
		const result = await scholarshipTitleApi.getAll();
		if (result && result.success && result.data) {
			setScholarshipTitleOptions(result.data.data.map(item => item.titleName));
		}
	}


	useEffect(() => {
		fetchList({
			...autoFilters,
			page: currentPage,
		});
	}, [currentPage]);

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	}, [itemsPerPage]);

	useEffect(() => {
		//= Lấy danh sách các danh hiệu khen thưởng để làm filter
		fetchScholarshipTitleOptions();
	}, []);

	// Reset to first page when filters change
	const handleFilterChange = (setter: Function, value: any) => {
		setter(value);
		setCurrentPage(1);
	};

	// Handle add new degree
	const handleAdd = async (newScholarship: Omit<Scholarship, "id" | "createdAt" | "updatedAt">) => {
		if (!canCreate) {
			notify.error("Bạn không có quyền tạo Khen thưởng - Chứng nhận");
			return;
		}

		try {
			const result = await scholarshipApi.create(newScholarship);

			notify.success("Thêm Khen thưởng - Chứng nhận thành công");
			await fetchList({});
			await fetchScholarshipTitleOptions();
			setHandleModalOpen(false);
		} catch (error) {
			console.error("Exception:", error);
		}
	};

	// Handle edit degree
	const handleEdit = (degree: Scholarship) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền chỉnh sửa Khen thưởng - Chứng nhận");
			return;
		}
		setEditingDegree(degree);
	};

	// Handle update degree
	const handleUpdate = async (updatedDegree: Scholarship) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền cập nhật Khen thưởng - Chứng nhận");
			return;
		}
		if (updatedDegree.id)
			try {
				setIsUpdating(true);
				const result = await scholarshipApi.update(updatedDegree.id, updatedDegree);

				if (result.success) {
					notify.success("Cập nhật Khen thưởng - Chứng nhận thành công");
					await fetchList({});
				} else {
					throw new Error("Lỗi khi cập nhật Khen thưởng - Chứng nhận");
				}
				await fetchScholarshipTitleOptions();
			} catch (error) {
				console.error("Exception:", error);
			} finally {
				setIsUpdating(false);
			}
	};

	// Handle delete degree
	const handleDelete = async () => {
		if (!canDelete) {
			notify.error("Bạn không có quyền xóa Khen thưởng - Chứng nhận");
			return;
		}
		if (deleteItemId == null) {
			notify.error("Chưa chọn Khen thưởng - Chứng nhận cần xóa");
			return;
		}

		try {
			setIsDeleting(true);
			await scholarshipApi.delete(deleteItemId);
			await fetchList({});
			await fetchScholarshipTitleOptions();
		} catch (error) {
			console.error("Exception:", error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleImport = async (data: any[]) => {
		if (!canImport) {
			notify.error("Bạn không có quyền import Khen thưởng - Chứng nhận");
			throw new Error("Không có quyền import");
		}

		try {
			const importedScholarshipData: any = data.map((row) => ({
				year: row["Năm học (*)"],
				semester: row["Học kỳ"] || "",
				major: row["Ngành (*)"],
				studentId: row["Mã sinh viên (*)"],
				studentName: row["Họ và tên (*)"],
				cccd: row["CCCD (*)"],
				dateOfBirth: row["Ngày sinh (*)"],
				// gpa: row["Điểm TB"],
				// conductScore: row["Điểm RL"],
				scholarshipCode: row["Mã KT-CN"] || "",
				scholarshipName: row["Tên KT-CN (*)"],
				title: row["Danh hiệu (*)"],
				// amount: row["Số tiền KT-CN"],
				certificateNo: row["Số quyết định (*)"],
				// percentLevel: row["Mức PT"],
				notes: row["Ghi chú"],
				errors: "",
			}));

			//= Gọi hàm kiểm tra từng hàng dữ liệu có khớp với yêu cầu ràng buộc không
			const validatedImportData = await validateScholarshipImportData(importedScholarshipData);

			//= Nếu có lỗi thì trả về dữ liệu import cho công cụ export để export ra file excel kèm lỗi
			const hasErrors = validatedImportData.some((x) => x.errors.length !== 0);
			if (hasErrors) {
				return validatedImportData;
			}
			//= Nếu không lỗi thì format lại dữ liệu rồi gửi dữ liệu import đến back-end
			const formattedImportData = validatedImportData.map((x) => {
				return { ...x, dateOfBirth: format(parse(x.dateOfBirth, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }) };
			});
			const result = await scholarshipApi.bulkImport(formattedImportData);

			await fetchList({});
			await fetchScholarshipTitleOptions();
		} catch (error) {
			console.error("Exception:", error);
		}
	};

	// Handle export
	const handleExport = useCallback(async () => {
		if (!canExport) {
			notify.error("Bạn không có quyền export Khen thưởng - Chứng nhận");
			return [];
		}

		const exportData = await getFullRecord(isFiltering ? autoFilters : {});

		return exportData.map((row) => ({
			"Năm học (*)": row.year,
			"Học kỳ": row.semester,
			"Ngành (*)": row.major,
			"Mã sinh viên (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth ? format(new Date(row.dateOfBirth), "dd/MM/yyyy") : "",
			"CCCD (*)": row.cccd,
			// "Điểm TB": row.gpa,
			// "Điểm RL": row.conductScore,
			"Mã KT-CN": row.scholarshipCode,
			"Tên KT-CN (*)": row.scholarshipName,
			"Danh hiệu (*)": row.title,
			// "Số tiền KT-CN": row.amount,
			"Số quyết định (*)": row.certificateNo,
			// "Mức PT": row.percentLevel,
			"Ghi chú": row.notes,
		}));
	}, [autoFilters, isFiltering, canExport]);

	const handleExportErrors = (data: any[]) => {
		// Chuyển đổi dữ liệu có lỗi sang định dạng phù hợp để xuất file, không nên biến đổi giá trị
		return data.map((row) => ({
			"Năm học (*)": row.year,
			"Học kỳ": row.semester,
			"Ngành (*)": row.major,
			"Mã sinh viên (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth,
			"CCCD (*)": row.cccd,
			// "Điểm TB": row.gpa,
			// "Điểm RL": row.conductScore,
			"Mã KT-CN": row.scholarshipCode,
			"Tên KT-CN (*)": row.scholarshipName,
			"Danh hiệu (*)": row.title,
			// "Số tiền KT-CN": row.amount,
			"Số quyết định (*)": row.certificateNo,
			// "Mức PT": row.percentLevel,
			"Ghi chú": row.notes,
			Lỗi: row.errors,
		}));
	};

	const clearFilters = () => {
		setIsFiltering(false);
		setMajorFilter("");
		setSearchTerm("");
		setSearchMajorName("");
		setMajors([]);
		setScholarshipTitleFilter("all");
		setSearchScholarshipTitleName("");
		setScholarshipTitle([]);

		fetchList({})
		fetchScholarshipTitleOptions();
	};

	const startFilters = () => {
		setIsFiltering(true);
		fetchList();
	};

	useEffect(() => {
		//= Chuẩn bị filters mỗi khi có thay đổi trong các giá trị lọc
		if (createdDateFromFilter && createdDateToFilter) {
			const fromDate = new Date(createdDateFromFilter);
			const toDate = new Date(createdDateToFilter);
			if (toDate < fromDate) {
				notify.error("Giá trị lọc ngày tạo Khen thưởng - Chứng nhận không hợp lệ");
				return;
			}
		}

		if (updatedDateFromFilter && updatedDateToFilter) {
			const fromDate = new Date(updatedDateFromFilter);
			const toDate = new Date(updatedDateToFilter);
			if (toDate < fromDate) {
				notify.error("Giá trị lọc ngày cập nhật Khen thưởng - Chứng nhận không hợp lệ");
				return;
			}
		}

		setAutoFilters({
			search: searchTerm.trim().toLocaleLowerCase(),
			major: majorFilter,
			title: scholarshipTitleFilter,
			createFrom: createdDateFromFilter,
			createTo: createdDateToFilter,
			updateFrom: updatedDateFromFilter,
			updateTo: updatedDateToFilter,
			page: currentPage,
			limit: itemsPerPage,
		});

	}, [itemsPerPage, searchTerm, scholarshipTitleFilter, currentPage, scholarshipTitle, createdDateFromFilter, createdDateToFilter, updatedDateFromFilter, updatedDateToFilter, majorFilter]);

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản Lý Khen Thưởng - Chứng Nhận</h2>
				</div>
				<div className='flex gap-2'>
					<Button
						variant='outline'
						onClick={() => {
							fetchList(autoFilters);
						}}
						disabled={loading}
					>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					{canCreate && (
						<Button
							variant={"add"}
							onClick={() => {
								setModalMode("create");
								setHandleModalOpen(true);
							}}
						>
							<Plus className='w-4 h-4' />
							Thêm mới
						</Button>
					)}
					{(canImport || canExport) && (
						<ImportExportActions
							onImport={canImport ? handleImport : async (_: any[], mode: ImportMode) => []}
							onExport={canExport ? handleExport : async () => []}
							onExportError={handleExportErrors}
							templateData={generateScholarshipTemplate()}
							entityName='Khen thưởng - Chứng nhận'
							sampleFileName={`DSKT-CN_${format(new Date(), "ddMMyyyy")}`}
							validHeaderImport={[
								"Năm học (*)",
								"Học kỳ",
								"Ngành (*)",
								"Mã sinh viên (*)",
								"Họ và tên (*)",
								"Ngày sinh (*)",
								"CCCD (*)",
								// "Điểm TB",
								// "Điểm RL",
								"Mã KT-CN",
								"Tên KT-CN (*)",
								"Danh hiệu (*)",
								// "Số tiền KT-CN",
								"Số quyết định (*)",
								// "Mức PT",
								"Ghi chú",
							]}
							dataType="scholarship"
							sampleErrorFileName={`DSKT-CN_Errors_${format(new Date(), "ddMMyyyy")}`}
						/>
					)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Danh Sách Khen thưởng - Chứng nhận</CardTitle>
					<div className='flex flex-wrap items-center gap-4 mt-4'>
						<div className='relative min-w-96'>
							<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
							<NgocInput placeholder='Tìm kiếm theo mã SV, tên, mã KT-CN, CCCD' value={searchTerm} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFilterChange(setSearchTerm, e.target.value)} maxLength={100} className="pl-8 min-w-[500px]" />
						</div>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("w-fit justify-between text-slate-700 font-normal")}>
									{scholarshipTitle.length === 1
										? scholarshipTitleOptions.find((x) => x === scholarshipTitle[0])
										: scholarshipTitle.length > 1
											? `Đã chọn ${scholarshipTitle.length} danh hiệu`
											: "Danh hiệu"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<div className='flex gap-1'>
									<input
										value={searchScholarshipTitleName}
										onInput={(e: any) => {
											setSearchScholarshipTitleName(e.target.value);
										}}
										type='text'
										className='border w-full px-1'
										placeholder='Tìm kiếm...'
										onKeyDown={(e) => {
											e.stopPropagation();
										}}
									/>
									<button
										onClick={() => {
											setSearchScholarshipTitleName("");
										}}
										type='button'
										className='cursor-pointer text-sm'
									>
										<RefreshCw size={"1rem"} />
									</button>
								</div>
								<div className='h-fit max-h-60 overflow-y-scroll'>
									{scholarshipTitleOptions
										.filter((title) => title.toLocaleLowerCase().includes(searchScholarshipTitleName.trim().toLowerCase()))
										.map((titleName) => (
											<DropdownMenuCheckboxItem
												key={titleName}
												checked={scholarshipTitle.includes(titleName)}
												onCheckedChange={() => toggleSelectScholarshipTitle(titleName)}
												className='cursor-pointer'
											>
												{titleName}
											</DropdownMenuCheckboxItem>
										))}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("w-fit justify-between text-slate-700 font-normal")}>
									{majors.length === 1 ? majorListForScholarship.find((major) => major === majors[0]) : majors.length > 1 ? `Đã chọn ${majors.length} nghành học` : "Ngành học"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<div className='flex gap-1'>
									<input
										value={searchMajorName}
										onInput={(e: any) => {
											setSearchMajorName(e.target.value);
										}}
										type='text'
										className='border w-full px-1'
										placeholder='Tìm kiếm...'
										onKeyDown={(e) => {
											e.stopPropagation();
										}}
									/>
									<button
										onClick={() => {
											setSearchMajorName("");
										}}
										type='button'
										className='cursor-pointer text-sm'
									>
										<RefreshCw size={"1rem"} />
									</button>
								</div>
								<div className='h-fit max-h-60 overflow-y-scroll'>
									{majorListForScholarship
										.filter((mj) => mj.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
										.map((majorName) => (
											<DropdownMenuCheckboxItem
												key={majorName}
												checked={majors.includes(majorName)}
												onCheckedChange={() => toggleSelectMajor(majorName)}
												className='capitalize cursor-pointer'
											>
												{majorName}
											</DropdownMenuCheckboxItem>
										))}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>

						{/* Lọc theo ngày tạo */}
						<div className='flex items-center gap-2'>
							<Popover open={createdDateFromFilterModal} onOpenChange={setCreatedDateFromFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{createdDateFromFilter ? format(createdDateFromFilter, "dd/MM/yyyy") : "Tạo từ ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={createdDateFromFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setCreatedDateFromFilter(localDate.toISOString());
											setCreatedDateFromFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
							<span className='text-muted-foreground'>-</span>
							<Popover open={createdDateToFilterModal} onOpenChange={setCreatedDateToFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{createdDateToFilter ? format(createdDateToFilter, "dd/MM/yyyy") : "Tạo đến ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={createdDateToFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setCreatedDateToFilter(localDate.toISOString());
											setCreatedDateToFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
						</div>
						{/* Lọc theo ngày cập nhật */}
						<div className='flex items-center gap-2'>
							<Popover open={updatedDateFromFilterModal} onOpenChange={setUpdatedDateFromFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{updatedDateFromFilter ? format(updatedDateFromFilter, "dd/MM/yyyy") : "Cập nhật từ ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={updatedDateFromFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setUpdatedDateFromFilter(localDate.toISOString());
											setUpdatedDateFromFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
							<span className='text-muted-foreground'>-</span>
							<Popover open={updatedDateToFilterModal} onOpenChange={setUpdatedDateToFilterModal}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{updatedDateToFilter ? format(updatedDateToFilter, "dd/MM/yyyy") : "Cập nhật đến ngày"}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={updatedDateToFilter as unknown as Date}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											// localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setUpdatedDateToFilter(localDate.toISOString());
											setUpdatedDateToFilterModal(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
						</div>

						{/* {((requestTypeFilter && requestTypeFilter !== "all") || (majorFilter && majorFilter !== "all") || yearFromFilter || yearToFilter) && (
						)} */}
						<Button size={"sm"} variant='edit' onClick={() => startFilters()}>
							Lọc
						</Button>
						<Button size={"sm"} variant='outline' onClick={clearFilters}>
							Đặt lại
						</Button>
					</div>
				</CardHeader >
				<CardContent>
					{/* Table with horizontal scroll */}
					<Select
						value={itemsPerPage.toString()}
						onValueChange={(value) => {
							setItemsPerPage(Number(value));
							fetchList({
								...autoFilters,
								limit: Number(value),
								page: 1
							});
						}}
					>
						<SelectTrigger className='w-20'>
							<SelectValue placeholder='Hình thức đào tạo' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='10'>10</SelectItem>
							<SelectItem value='15'>15</SelectItem>
							<SelectItem value='20'>20</SelectItem>
							<SelectItem value='50'>50</SelectItem>
						</SelectContent>
					</Select>
					<div className='rounded-md border mt-2 relative'>
						<SmoothLoadingWrapper loading={loading} >
							<Table className="min-w-[1200px]">
								<TableHeader>
									<TableRow>
										<TableHead className='w-[140px]'>Mã sinh viên</TableHead>
										<TableHead className='w-[150px]'>Họ tên</TableHead>
										<TableHead className='w-[130px]'>CCCD</TableHead>
										<TableHead className='w-[180px]'>Ngành</TableHead>
										<TableHead className='w-[130px]'>Mã KT-CN</TableHead>
										<TableHead className='w-[130px]'>Tên KT-CN</TableHead>
										<TableHead className='w-[130px]'>Danh hiệu</TableHead>
										<TableHead className='w-[130px]'>Năm học</TableHead>
										<TableHead className='w-[130px]'>Học kỳ</TableHead>
										<TableHead className='w-[100px]'>Ngày tạo</TableHead>
										<TableHead className='w-[100px]'>Ngày cập nhật</TableHead>
										<TableHead className='w-[50px]'>Thao tác</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{
										degrees.length === 0 ? (
											<TableRow>
												<TableCell colSpan={12} className='text-center py-8 text-muted-foreground'>
													{searchTerm || scholarshipTitleFilter ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
												</TableCell>
											</TableRow>
										) : (
											degrees.map((degree) => (
												<TableRow key={degree.id}>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word' }}>{degree.studentId}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>{degree.studentName}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.cccd}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.major}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word' }}>{degree.scholarshipCode}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word' }}>{degree.scholarshipName}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word' }}>{degree.title}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.year}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.semester}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.createdAt ? format(new Date(degree.createdAt), "dd/MM/yyyy") : ""}</TableCell>
													<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.updatedAt ? format(new Date(degree.updatedAt), "dd/MM/yyyy") : ""}</TableCell>

													<TableCell>
														<div className='flex items-center gap-1'>
															<Button
																onClick={() => {
																	setDegreeInformation(degree);
																}}
																variant='ghost'
																className='h-8 w-8 p-0'
															>
																<Eye className='w-4 h-4' />
															</Button>
															{canUpdate && (
																<Button
																	variant='ghost'
																	onClick={() => {
																		setModalMode("edit");
																		handleEdit(degree);
																		setHandleModalOpen(true);
																	}}
																	className='h-8 w-8 p-0 text-yellow-600 hover:text-yellow-600'
																	disabled={isUpdating}
																>
																	<Edit className='w-4 h-4' />
																</Button>
															)}
															{canDelete && (
																<Button variant='ghost' onClick={() => setDeleteItemId(degree.id)} className='h-8 w-8 p-0 text-destructive hover:text-destructive' disabled={isDeleting}>
																	{isDeleting ? <Loader2 className='w-4 h-4 animate-spin' /> : <Trash2 className='w-4 h-4' />}
																</Button>
															)}
														</div>
													</TableCell>
												</TableRow>
											))
										)
									}
								</TableBody>
							</Table>
						</SmoothLoadingWrapper>
					</div>

					{/* Pagination */}
					{!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
				</CardContent>
			</Card >

			{/* Handle Modal */}
			< ScholarshipModal isOpen={isHandleModalOpen} onClose={() => setHandleModalOpen(false)
			} onAdd={handleAdd} editData={editingDegree} onUpdate={handleUpdate} mode={modalMode} />

			<ScholarshipDetail data={degreeInformation} setDegreeInformation={setDegreeInformation} />

			<AlertDialog open={deleteItemId !== undefined} onOpenChange={() => setDeleteItemId(undefined)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div >
	);
}
